import React, { useState, useEffect } from 'react';
import '../IpPatientDetails.css';
import {
    Box, TextField, Button, Stack, Grid, Autocomplete,
    Dialog, Tooltip, Typography, IconButton, InputAdornment,
    Menu, MenuItem, FormControl, InputLabel, Select,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import {
    CommonPatientDetails, CommonPatientCard, ConsultantPatientCard,
    DeletePopup, CommonPopUp, CommonEditButton, CommonDeleteButton
} from '../../../../../Components/Common Components/CommonComponents';
import CommonValidation from '../../../../../Components/CommonFunctions/CommonValidation';
import { ImagePaths } from '../../../../../Utility/ImagePaths';
import { Colors } from '../../../../../Styles/Colors';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import { convertDateTime, CheckAccessFunc } from '../../../../../Components/CommonFunctions/CommonFunctions';
import Loader from '../../../../../Components/Loader';
import { DateTime } from 'luxon';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const MonitoringChart = (props) => {
    const { t } = useTranslation();

    // patient
    const [patientData, setPatientData] = useState({});
    const [attenderData, setAttenderData] = useState({});
    const [withOutAppointment, setWithOutAppointment] = useState({});
    const [clinicId, setClinicId] = useState(null);
    // nurse
    const [NurseList, setNurseList] = useState([]);
    const [nurseName, setNurseName] = useState(null);
    // date
    const [monitoringDate, setMonitoringDate] = useState(new Date());
    const [DateOpen, setDateOpen] = useState(false);
    // popup
    const [openPopup, setOpenPopup] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    // data / pagination
    const [monitoringData, setMonitoringData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    // loader / toast
    const [isLoader, setIsLoader] = useState(false);
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState('');
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [dateError, setDateError] = useState(false);
    // delete
    const [deletePopup, setDeletePopup] = useState(false);
    const [delSelectedId, setDelSelectedId] = useState(null);
    // history menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isSelected, setIsSelected] = useState(false);
    const [isHistoryClicked, setIsHistoryClicked] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    // form fields
    const [temperature, setTemperature] = useState('');
    const [temp_unit, setTempUnit] = useState('°F');
    const [periphery, setPeriphery] = useState('');
    const [heater_output, setHeaterOutput] = useState('');
    const [hr, setHr] = useState('');
    const [rr, setRr] = useState('');
    const [sat, setSat] = useState('');
    const [crt, setCrt] = useState('');
    const [bp, setBp] = useState('');
    const [activity, setActivity] = useState('');
    const [suction, setSuction] = useState('');
    const [aspirate, setAspirate] = useState('');
    const [feed, setFeed] = useState('');
    const [abd_girth, setAbdGirth] = useState('');
    const [fluid, setFluid] = useState('');
    const [iv_site, setIvSite] = useState('');
    const [rbs, setRbs] = useState('');
    const [urine, setUrine] = useState('');
    const [stool, setStool] = useState('');
    const [investigation, setInvestigation] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        const foClinicID = JSON.parse(localGetItem('loggedInUserInfo') || '{}');
        const selectedClinic = JSON.parse(localGetItem('SelectedDoctorClinicId')) || foClinicID?.clinic_id;
        const patientDetails = getCachevalue('IppatientData');
        const patients = JSON.parse(patientDetails || '{}');
        const appointmentDetail = getCachevalue('withoutAppointment');
        const details = JSON.parse(appointmentDetail || '{}');

        setClinicId(selectedClinic);
        getNurseList(selectedClinic);

        if (details?.patient_id === patients?.patient_id) {
            setWithOutAppointment(details);
            setPatientData(patients);
            getPatientData(patients, details, selectedClinic);
            getMonitoringData(patients, details, selectedClinic);
        } else {
            setPatientData(patients);
            getPatientData(patients, {}, selectedClinic);
            getMonitoringData(patients, {}, selectedClinic);
        }
    }, []);

    const errorMsgFunc = (message) => { setIsErrorMsg(true); setIsErrorMsgText(message); };
    const successMsgFunc = (message) => { setSuccessMsg(true); setSuccessMsgText(message); };
    const msgClose = () => { setIsErrorMsg(false); setIsErrorMsgText(''); setSuccessMsg(false); setSuccessMsgText(''); };

    const getNurseList = (cId) => {
        try {
            RestAPIService.getAll(Serviceurls.FO_STAFF_LIST + `?clinic_id=${cId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const list = response.data.data || [];
                        setNurseList(list);
                        if (list.length && localGetItem('NurseId')) {
                            const nurseId = JSON.parse(localGetItem('NurseId'));
                            const found = list.find((item) => item.staff_id === nurseId);
                            setNurseName(found || null);
                        }
                    }
                }).catch((error) => errorMsgFunc(error?.response?.data?.message || error.message));
        } catch (error) { errorMsgFunc(error.message); }
    };

    const getIpAdmissionId = () => {
        return patientData?.id || patientData?.ip_admission_id || (() => { try { const j = JSON.parse(getCachevalue('IppatientData') || 'null'); return j?.id || j?.ip_admission_id } catch { return null } })() || null;
    }

    const getPatientData = (pData, withoutAppt, cId) => {
        const admission_id = pData?.id || withoutAppt?.ip_admission_id || getIpAdmissionId();
        let url = `${(localGetItem('isNewBilling') === 'false' ? Serviceurls.IP_PATIENT_DETAILS : Serviceurls.IP_PATIENT_DETAILS_NEW)}?admission_id=${admission_id}`;
        if (props.is_From_IpNotes && cId) url = `${url}&clinic_id=${cId}`;
        try {
            RestAPIService.getAll(url)
                .then((response) => {
                    if (response.data.status === 'success') setAttenderData(response.data.data[0]);
                }).catch((error) => errorMsgFunc(error?.response?.data?.message || error.message));
        } catch (error) { errorMsgFunc(error.message); }
    };

    const getMonitoringData = (pData, withoutAppt, cId) => {
        const admission_id = pData?.id || withoutAppt?.ip_admission_id;
        setIsLoader(true);
        try {
            RestAPIService.getAll(`${Serviceurls.IP_MONITORING_CHART}?ip_admission_id=${admission_id}&clinic_id=${cId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setMonitoringData(response.data.data || []);
                        setIsLoader(false);
                    }
                }).catch((error) => { setIsLoader(false); errorMsgFunc(error?.response?.data?.message || error.message); });
        } catch (error) { setIsLoader(false); errorMsgFunc(error.message); }
    };

    const refreshData = () => getMonitoringData(patientData, withOutAppointment, clinicId);

    const sortHistoryByModifiedTimeDesc = (list) => {
        return [...(list || [])].sort((a, b) => {
            const ta = a.modified_date_to || a.modified_date_from || '';
            const tb = b.modified_date_to || b.modified_date_from || '';
            return String(tb).localeCompare(String(ta));
        });
    };

    const getHistory = (id) => {
        try {
            RestAPIService.getAll(`${Serviceurls.FO_IP_HISTORY}?condition=monitor&row_id=${id}&clinic_id=${clinicId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        setHistoryList(sortHistoryByModifiedTimeDesc(response.data.data || []));
                        setIsHistoryClicked(true);
                    }
                }).catch((error) => errorMsgFunc(error?.response?.data?.message || error.message));
        } catch (error) { errorMsgFunc(error.message); }
    };

    const handleSave = () => {
        const hasData = [temperature, periphery, heater_output, hr, rr, sat, crt, bp, activity,
            suction, aspirate, feed, abd_girth, fluid, iv_site, rbs, urine, stool, investigation
        ].some(v => v !== '' && v != null);
        if (!hasData) return errorMsgFunc(t('Please, Add Atleast One Value'));
        const admission_id = patientData?.id || withOutAppointment?.ip_admission_id;
        const myUser = JSON.parse(localGetItem('loggedInUserInfo') || '{}');
        const entry_time = DateTime.fromJSDate(monitoringDate ? new Date(monitoringDate) : new Date()).toFormat('yyyy-MM-dd HH:mm:ss');
        const data = {
            clinic_id: clinicId,
            ip_admission_id: admission_id,
            patient_id: patientData?.patient_id || withOutAppointment?.patient_id || null,
            nurse_id: nurseName?.staff_id || null,
            doctor_id: attenderData?.doctor_id || myUser?.doctor_id || null,
            entry_time,
            temperature: temperature ? `${temperature} ${temp_unit.replace('°', '')}` : null,
            periphery: CommonValidation.removeSpace(periphery) || null,
            heater_output: heater_output || null,
            hr: hr || null,
            rr: rr || null,
            sat: sat || null,
            crt: crt || null,
            bp: CommonValidation.removeSpace(bp) || null,
            activity: activity || null,
            suction: suction || null,
            aspirate: aspirate || null,
            feed: feed || null,
            abd_girth: abd_girth || null,
            fluid: fluid || null,
            iv_site: CommonValidation.removeSpace(iv_site) || null,
            rbs: CommonValidation.removeSpace(rbs) || null,
            urine: urine || null,
            stool: CommonValidation.removeSpace(stool) || null,
            investigation: CommonValidation.removeSpace(investigation) || null,
            remarks: CommonValidation.removeSpace(remarks) || null,
        };
        if (isEdit && selectedId) data.id = selectedId;
        setIsSaving(true);
        try {
            RestAPIService.create(data, Serviceurls.IP_MONITORING_CHART + `?clinic_id=${clinicId}`)
                .then((response) => {
                    if (response.data.status === 'success') {
                        handleClose(); refreshData(); successMsgFunc(response.data.message);
                    } else { setIsSaving(false); errorMsgFunc(response.data.message); }
                }).catch((error) => { setIsSaving(false); errorMsgFunc(error?.response?.data?.message || error.message); });
        } catch (error) { setIsSaving(false); errorMsgFunc(error.message); }
    };

    const popupClose = () => { setDeletePopup(false); setDelSelectedId(null); };

    const handleDelete = (item) => { setDeletePopup(true); setDelSelectedId(item?.id); };

    const confirmDelete = () => {
        try {
            RestAPIService.delete(Serviceurls.IP_MONITORING_CHART + `?id=${delSelectedId}`)
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        successMsgFunc(response?.data?.message);
                        setDeletePopup(false); setDelSelectedId(null); refreshData();
                    }
                }).catch((error) => errorMsgFunc(error?.response?.data?.message || error.message));
        } catch (error) { errorMsgFunc(error.message); }
    };

    const onEditHandler = (item) => {
        const existingNurse = NurseList.find(n => n.staff_id === item.nurse_profile_detail_id);
        const parsedDate = item?.entry_time ? DateTime.fromISO(item.entry_time).toJSDate() : new Date();
        setOpenPopup(true); setIsEdit(true); setSelectedId(item?.id);
        setMonitoringDate(parsedDate); setNurseName(existingNurse || null);
        const rawTemp = item?.temperature ? String(item.temperature) : '';
        const tempMatch = rawTemp.match(/^(\d+\.?\d*)\s*(.*)?$/);
        setTemperature(tempMatch ? tempMatch[1] : rawTemp);
        const rawUnit = (tempMatch && tempMatch[2]) ? tempMatch[2] : (item?.temp_unit || '°F');
        setTempUnit(rawUnit.startsWith('°') ? rawUnit : `°${rawUnit}`);
        setPeriphery(item?.periphery || ''); setHeaterOutput(item?.heater_output || '');
        setHr(item?.hr || ''); setRr(item?.rr || ''); setSat(item?.sat || '');
        setCrt(item?.crt || ''); setBp(item?.bp || ''); setActivity(item?.activity || '');
        setSuction(item?.suction || ''); setAspirate(item?.aspirate || '');
        setFeed(item?.feed || ''); setAbdGirth(item?.abd_girth || '');
        setFluid(item?.fluid || ''); setIvSite(item?.iv_site || '');
        setRbs(item?.rbs || ''); setUrine(item?.urine || ''); setStool(item?.stool || '');
        setInvestigation(item?.investigation || ''); setRemarks(item?.remarks || '');
    };

    const handleClose = () => { handleClear(); setOpenPopup(false); };

    const handleClear = () => {
        setIsEdit(false); setSelectedId(null); setIsSaving(false); setDateError(false);
        setMonitoringDate(new Date()); setTemperature(''); setTempUnit('°F');
        setPeriphery(''); setHeaterOutput(''); setHr(''); setRr(''); setSat('');
        setCrt(''); setBp(''); setActivity(''); setSuction(''); setAspirate('');
        setFeed(''); setAbdGirth(''); setFluid(''); setIvSite(''); setRbs('');
        setUrine(''); setStool(''); setInvestigation(''); setRemarks('');
        if (NurseList?.length && localGetItem('NurseId')) {
            const nurseId = JSON.parse(localGetItem('NurseId'));
            setNurseName(NurseList.find((item) => item.staff_id === nurseId) || null);
        } else { setNurseName(null); }
    };

    const renderNumericField = (label, value, setter, unit = '', maxLen = 20) => (
        <TextField
            size='small' label={t(label)} value={value} fullWidth
            onChange={(e) => { const val = e.target.value; if (val === '' || (CommonValidation.DecimalNumber(val) && val.length <= maxLen)) setter(val); }}
            InputProps={unit ? { endAdornment: <InputAdornment position='end'><Typography variant='caption'>{unit}</Typography></InputAdornment> } : {}}
        />
    );

    const renderTextField = (label, value, setter, maxLen = 200) => (
        <TextField size='small' label={t(label)} value={value} onChange={(e) => { if (e.target.value.trimStart().length <= maxLen) setter(e.target.value.trimStart()); }} fullWidth />
    );

    const historyPopUpClose = () => { setIsHistoryClicked(false); setHistoryList([]); };

    const renderMenuItems = (row) => (
        <div>
            <Tooltip title={t('More')} placement='top' arrow>
                <IconButton size='small' onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedRow(row.id); setIsSelected(true); }}>
                    <MoreVertIcon fontSize='small' />
                </IconButton>
            </Tooltip>
            {(isSelected && selectedRow === row.id) ?
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => { setAnchorEl(null); getHistory(row.id); }}>{t('History')}</MenuItem>
                </Menu> : null}
        </div>
    );

    const renderHistoryContent = () => {
        const historyFieldSortValue = (row, key) => {
            const v = row[`${key}_to`] !== undefined && row[`${key}_to`] !== null ? row[`${key}_to`] : row[`${key}_from`];
            if (v === undefined || v === null || v === '') return null;
            if (typeof v === 'number' && !Number.isNaN(v)) return v;
            const n = Number(v);
            const s = String(v).trim();
            if (s !== '' && !Number.isNaN(n) && s === String(n)) return n;
            return String(v);
        };
        const compareHistorySort = (a, b) => {
            if (a == null && b == null) return 0;
            if (a == null) return 1;
            if (b == null) return -1;
            if (typeof a === 'number' && typeof b === 'number') return a - b;
            return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        };
        const historyFields = [
            { key: 'temperature', label: 'Temperature', minWidth: 110 },
            { key: 'periphery', label: 'Periphery', minWidth: 110 },
            { key: 'heater_output', label: 'Heater Output', minWidth: 120 },
            { key: 'hr', label: 'HR', minWidth: 90 },
            { key: 'rr', label: 'RR', minWidth: 90 },
            { key: 'sat', label: 'SAT', minWidth: 90 },
            { key: 'crt', label: 'CRT', minWidth: 90 },
            { key: 'bp', label: 'BP', minWidth: 100 },
            { key: 'activity', label: 'Activity', minWidth: 100 },
            { key: 'suction', label: 'Suction', minWidth: 100 },
            { key: 'aspirate', label: 'Aspirate', minWidth: 100 },
            { key: 'feed', label: 'Feed', minWidth: 90 },
            { key: 'abd_girth', label: 'Abd. Girth', minWidth: 110 },
            { key: 'fluid', label: 'Fluid', minWidth: 90 },
            { key: 'iv_site', label: 'I.V. Site', minWidth: 100 },
            { key: 'rbs', label: 'RDS', minWidth: 90 },
            { key: 'urine', label: 'Urine', minWidth: 90 },
            { key: 'stool', label: 'Stool', minWidth: 90 },
            { key: 'investigation', label: 'Investigation', minWidth: 120 },
            { key: 'remarks', label: 'Remarks', minWidth: 110 },
        ];
        const rows = (historyList || []).map((item, idx) => ({ ...item, sno: idx + 1 }));
        const fmtVal = (val, iso) => {
            if (val == null) return '-';
            return iso ? convertDateTime(String(val)) : String(val);
        };
        const mkCell = (params, f) => {
            const from = fmtVal(params.row[`${f.key}_from`], f.iso);
            const to = fmtVal(params.row[`${f.key}_to`], f.iso);
            if (from === '-' && to === '-') return <Box component='div'>-</Box>;
            const text = `${from} | ${to}`;
            return (
                <Tooltip placement='top' title={text} arrow>
                    <Box component='div' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</Box>
                </Tooltip>
            );
        };
        const columns = [
            {
                field: 'modified_by_to', headerName: t('Modified By'), minWidth: 130,
                valueGetter: (params) => params.row.modified_by_to || params.row.modified_by_from || '',
                sortComparator: (v1, v2) => String(v1 || '').localeCompare(String(v2 || ''), undefined, { sensitivity: 'base' }),
                renderCell: (params) => <Box component='div'>{params.row.modified_by_to || '-'}</Box>
            },
            {
                field: 'modified_date', headerName: t('Modified Date And Time'), minWidth: 160, headerAlign: 'center', align: 'center',
                valueGetter: (params) => params.row.modified_date_to || params.row.modified_date_from || '',
                sortComparator: (v1, v2) => String(v1 || '').localeCompare(String(v2 || '')),
                renderCell: (params) => <Box component='div'>{params.row.modified_date || '-'}</Box>
            },
            ...historyFields.map(f => ({
                field: f.key,
                headerName: f.label,
                minWidth: f.minWidth,
                valueGetter: (params) => historyFieldSortValue(params.row, f.key),
                sortComparator: (v1, v2) => compareHistorySort(v1, v2),
                renderCell: (params) => mkCell(params, f)
            }))
        ];
        return (
            <Box component='div' className='eMed_history_nurse_grid' style={{ width: '86vw' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.sno}
                    density='compact'
                    hideFooter
                    components={{
                        NoRowsOverlay: () => (
                            <Box className='eMed_conf_nodata'>{t('NoRecordsFound')}</Box>
                        )
                    }}
                />
            </Box>
        );
    };

    const currentDate = new Date();
    const admissionDate = patientData?.admitted_date ? new Date(patientData.admitted_date)
        : patientData?.admission_date ? new Date(patientData.admission_date) : currentDate;
    const dischargeDate = patientData?.discharge_date && (new Date(patientData.discharge_date) <= currentDate)
        ? new Date(patientData.discharge_date) : currentDate;
    const attenderInfo = {
        lineOne: t('AttenderName'), lineTwo: `${attenderData?.attender_name || '-'}`,
        lineFour: t('ContactNumber'), LineFive: `${attenderData?.attender_mobile || '-'}`
    };
    const Consultant = {
        image: ImagePaths.Doctor_Icon.default, width: '2.3vw', height: '2.3vw',
        lineOne: t('ConsultantName'), lineTwo: `${attenderData?.doctor_name || '-'}`
    };

    const renderCell = (value, limit) => {
        const str = value != null && value !== '' ? String(value) : null;
        const display = str ? (str.length > limit ? str.slice(0, limit) + '...' : str) : '-';
        return <Tooltip placement='top' title={str || ''}><Typography variant='body2'>{display}</Typography></Tooltip>;
    };
    const renderFullCell = (value) => {
        const str = value != null && value !== '' ? String(value) : '-';
        return <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>{str}</Typography>;
    };
    const paginatedRows = monitoringData.slice(page * pageSize, page * pageSize + pageSize);
    const checkuserAccess = props.is_From_IpNotes ? false :
        CheckAccessFunc('front_office', 'In Patients', 'Clinical Notes', 'Monitoring Chart', 'SubTab')?.editAccess ?
            withOutAppointment?.is_ip : true;

    const thSx = { backgroundColor: Colors.DataTblHeaderbg, fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', py: '7px', px: '14px' };
    const tdSx = { fontSize: '0.85rem', py: '5px', px: '14px' };
    const stickyLTh = { ...thSx, position: 'sticky', left: 0, zIndex: 5 };
    const stickyRTh = { ...thSx, position: 'sticky', right: 0, zIndex: 5 };
    const stickyLTd = { ...tdSx, position: 'sticky', left: 0, zIndex: 1, backgroundColor: Colors.white, 'tr:hover &': { backgroundColor: '#f5f5f5' } };
    const stickyRTd = { ...tdSx, position: 'sticky', right: 0, zIndex: 1, backgroundColor: Colors.white, 'tr:hover &': { backgroundColor: '#f5f5f5' } };

    return (
        <Box sx={{ backgroundColor: Colors.configHomeBg, height: '74dvh', p: '0.625vw' }}>
            <Grid container>
                <Stack className='eMed_medicationCard_header' direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
                    <Stack direction='row' justifyContent='space-evenly' alignItems='center' spacing={1.2}>
                        <Stack className='eMed_recCard_div' sx={{ width: '23vw', backgroundColor: Colors.white }}>
                            <CommonPatientDetails data={patientData} showDetailed={true} />
                        </Stack>
                        <CommonPatientCard details={attenderInfo} showDetailed={true} />
                        <ConsultantPatientCard name={Consultant} width={'20dvw'} />
                    </Stack>
                    <Button variant='contained' sx={{ textTransform: 'capitalize', height: '4.5dvh', width: '10dvw' }} disabled={checkuserAccess} onClick={() => setOpenPopup(true)}>
                        {t('Add')}
                    </Button>
                </Stack>
            </Grid>

            <Box sx={{ height: '65dvh', display: 'flex', flexDirection: 'column', backgroundColor: Colors.white }}>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader size='small' sx={{ width: 'max-content', minWidth: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={stickyLTh}>Date & Time</TableCell>
                                <TableCell sx={thSx}>Temperature (°F/°C)</TableCell>
                                <TableCell sx={thSx}>Periphery</TableCell>
                                <TableCell sx={thSx}>Heater Output (%)</TableCell>
                                <TableCell sx={thSx}>HR (bpm)</TableCell>
                                <TableCell sx={thSx}>RR (bpm)</TableCell>
                                <TableCell sx={thSx}>SAT (%)</TableCell>
                                <TableCell sx={thSx}>CRT (sec)</TableCell>
                                <TableCell sx={thSx}>BP (mmHg)</TableCell>
                                <TableCell sx={thSx}>Activity</TableCell>
                                <TableCell sx={thSx}>Suction (ml)</TableCell>
                                <TableCell sx={thSx}>Aspirate (ml)</TableCell>
                                <TableCell sx={thSx}>Feed (ml)</TableCell>
                                <TableCell sx={thSx}>Abd.Girth (cm)</TableCell>
                                <TableCell sx={thSx}>Fluid (ml)</TableCell>
                                <TableCell sx={thSx}>I.V. Site</TableCell>
                                <TableCell sx={thSx}>RDS</TableCell>
                                <TableCell sx={thSx}>Urine (ml)</TableCell>
                                <TableCell sx={thSx}>Stool</TableCell>
                                <TableCell sx={thSx}>Investigation/X-ray</TableCell>
                                <TableCell sx={thSx}>Remarks</TableCell>
                                <TableCell sx={stickyRTh} align='center'>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={21} align='center' sx={{ py: 3 }}>{t('NoRecordsFound')}</TableCell>
                                </TableRow>
                            ) : paginatedRows.map((row, i) => {
                                return (
                                    <TableRow key={row.id || i} hover>
                                        <TableCell sx={stickyLTd}>{renderFullCell(row.entry_time ? convertDateTime(row.entry_time) : null)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.temperature)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.periphery, 8)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.heater_output)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.hr)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.rr)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.sat)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.crt)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.bp)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.activity)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.suction)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.aspirate)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.feed)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.abd_girth)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.fluid)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.iv_site, 4)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.rbs, 4)}</TableCell>
                                        <TableCell sx={tdSx}>{renderFullCell(row.urine)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.stool, 4)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.investigation, 15)}</TableCell>
                                        <TableCell sx={tdSx}>{renderCell(row.remarks, 15)}</TableCell>
                                        <TableCell sx={stickyRTd} align='center'>
                                            <Box display='flex' alignItems='center' justifyContent='center'>
                                                <CommonEditButton size='1.5vw' disable={checkuserAccess} onClick={() => onEditHandler(row)} />
                                                <CommonDeleteButton size='1.5vw' disable={checkuserAccess} onClick={() => handleDelete(row)} />
                                                {renderMenuItems(row)}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 30]}
                    component='div'
                    count={monitoringData.length}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={(_e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Box>

            {/* Add / Edit Dialog */}
            <Dialog open={openPopup} maxWidth='md' fullWidth>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '1rem', py: '0.5rem', backgroundColor: Colors.DataTblHeaderbg }}>
                        <Typography fontWeight={'600'} fontSize={'0.95rem'}>{t('Monitoring Chart')}</Typography>
                        <IconButton size='small' onClick={handleClose}><CloseIcon /></IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', px: '1rem', py: '0.75rem' }}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <Box display='flex' gap={1}>
                                    <TextField
                                        size='small' label={t('Temperature')} value={temperature} sx={{ flex: 1 }}
                                        onChange={(e) => { const val = e.target.value; if (val === '' || (CommonValidation.DecimalNumber(val) && val.length <= 5)) setTemperature(val); }}
                                    />
                                    <FormControl size='small' sx={{ minWidth: '70px' }}>
                                        <InputLabel>{t('Unit')}</InputLabel>
                                        <Select label={t('Unit')} value={temp_unit} onChange={(e) => setTempUnit(e.target.value)}>
                                            <MenuItem value='°C'>°C</MenuItem>
                                            <MenuItem value='°F'>°F</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>{renderTextField('Periphery', periphery, setPeriphery, 20)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Heater Output', heater_output, setHeaterOutput, '%', 5)}</Grid>
                            <Grid item xs={6}>{renderNumericField('HR (Heart Rate)', hr, setHr, 'bpm', 3)}</Grid>
                            <Grid item xs={6}>{renderNumericField('RR (Respiratory Rate)', rr, setRr, 'bpm', 3)}</Grid>
                            <Grid item xs={6}>{renderNumericField('SAT (SpO₂)', sat, setSat, '%', 5)}</Grid>
                            <Grid item xs={6}>{renderNumericField('CRT (Cap. Refill Time)', crt, setCrt, 'sec', 4)}</Grid>
                            <Grid item xs={6}>{renderNumericField('BP (Blood Pressure)', bp, setBp, 'mmHg', 10)}</Grid>
                            <Grid item xs={6}>
                                <FormControl size='small' fullWidth>
                                    <InputLabel>{t('Activity')}</InputLabel>
                                    <Select label={t('Activity')} value={activity} onChange={(e) => setActivity(e.target.value)}>
                                        <MenuItem value='Active'>{t('Active')}</MenuItem>
                                        <MenuItem value='Lethargic'>{t('Lethargic')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>{renderNumericField('Suction', suction, setSuction, 'ml', 6)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Aspirate', aspirate, setAspirate, 'ml', 6)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Feed', feed, setFeed, 'ml', 6)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Abd. Girth (Abdominal Girth)', abd_girth, setAbdGirth, 'cm', 6)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Fluid', fluid, setFluid, 'ml', 6)}</Grid>
                            <Grid item xs={6}>{renderTextField('I.V. Site', iv_site, setIvSite, 30)}</Grid>
                            <Grid item xs={6}>{renderTextField('RDS (Respiratory Distress)', rbs, setRbs, 30)}</Grid>
                            <Grid item xs={6}>{renderNumericField('Urine', urine, setUrine, 'ml', 6)}</Grid>
                            <Grid item xs={6}>{renderTextField('Stool', stool, setStool, 30)}</Grid>
                            <Grid item xs={6}>{renderTextField('Investigation / X-ray', investigation, setInvestigation, 100)}</Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    size='small' options={NurseList || []} getOptionLabel={(option) => option.nurse || ''}
                                    value={nurseName} onChange={(_e, val) => setNurseName(val)} fullWidth
                                    renderInput={(params) => <TextField {...params} label={t('Nurse Name')} />}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField size='small' label={t('Remarks')} value={remarks} multiline rows={3} fullWidth
                                    onChange={(e) => { if (e.target.value.trimStart().length <= 100) setRemarks(e.target.value.trimStart()); }} />
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        inputFormat='DD-MM-YYYY & hh:mm A'
                                        value={monitoringDate}
                                        minDate={admissionDate}
                                        maxDate={dischargeDate}
                                        maxDateTime={dischargeDate}
                                        disableFuture
                                        onError={(err) => setDateError(!!err)}
                                        onChange={(newVal) => setMonitoringDate(newVal === null ? new Date() : newVal)}
                                        renderInput={(params) => (
                                            <TextField size='small' {...params} variant='standard' fullWidth />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, px: '1rem', py: '0.75rem', backgroundColor: Colors.DataTblHeaderbg, borderTop: `1px solid ${Colors.lightGray || '#e0e0e0'}` }}>
                        <Button variant='outlined' size='small' id='eMed_recPayment_btn' onClick={handleClear}>{t('Clear')}</Button>
                        <Button variant='contained' size='small' id='eMed_recPayment_btn' disabled={isSaving || dateError}
                            onClick={() => { handleSave(); }}>{t('Add')}</Button>
                    </Box>
                </Box>
            </Dialog>

            {deletePopup && <DeletePopup DeletTitle={`${t('deleteMsg')} ?`} deleteAlertPopupClose={popupClose} removeData={confirmDelete} />}
            {isHistoryClicked && <CommonPopUp width={'90vw'} backgroundColor={Colors.white} popUpClose={historyPopUpClose} title={t('History')} component={renderHistoryContent} />}
            {isErrorMsg && <ToastMsg severity='error' msg={isErrorMsgText} msgPop={msgClose} />}
            {successMsg && <ToastMsg severity='success' msg={successMsgText} msgPop={msgClose} />}
            <Loader loaderOpen={isLoader} />
        </Box>
    );
};

export default MonitoringChart;
